import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {AdminIntegrationService} from '../../../services/admin/admin-integration.service';
import {AdminCompetitionService} from '../../../services/admin/admin-competition.service';
import {
  CompetitionAwardTypes,
  IntegrationCompetitionAwardModel,
  IntegrationModel,
  IntegrationTypes,
  ObstacleCourseLevels,
  QRCodeContentTypes,
  RewardGrantTypes
} from '../../../models/qrcode-integrations/integration.model';
import {EggRewardModel, ItemRewardModel, MonsterRewardModel, RewardTypes} from '../../../models/qrcode-integrations/reward.model';
import {CompetitionModel} from '../../../models/competition/competition.model';
import {ErrorModel} from '../../../models/shared/error.model';
import {ModalService} from '../../../shared/modals/modal/modal.service';

export interface EditorOption {
  id: string;
  label: string;
}

/**
 * Edits an award, either a standalone award or a competition award. Used by the integrations page
 * and by the goals page of a competition.
 *
 * The editor always works on a competition award model, which is the award model plus the
 * competition and goal ids. The ids are ignored by the backend for a standalone award.
 */
@Component({
  selector: 'app-admin-integration-award-editor',
  templateUrl: './admin-integration-award-editor.component.html',
  styleUrls: ['./admin-integration-award-editor.component.css']
})
export class AdminIntegrationAwardEditorComponent implements OnChanges {

  @Input() award: IntegrationCompetitionAwardModel;
  @Input() competitions: CompetitionModel[] = [];

  /** Lets the user pick between a standalone award and a competition award. */
  @Input() canChooseType = false;

  @Output() saved = new EventEmitter<IntegrationModel>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<void>();

  typeOptions: EditorOption[] = [
    {id: IntegrationTypes.competitionAward, label: 'Competition award'},
    {id: IntegrationTypes.award, label: 'Award (not tied to a competition)'}
  ];

  awardTypeOptions: EditorOption[] = [
    {id: CompetitionAwardTypes.award, label: 'Plain award'},
    {id: CompetitionAwardTypes.obstacleCourse, label: 'Obstacle course'}
  ];

  levelOptions: EditorOption[] = [];
  grantOptions: EditorOption[] = [
    {id: RewardGrantTypes.grantAll, label: 'Give all rewards'},
    {id: RewardGrantTypes.pickOne, label: 'Student picks one'}
  ];

  contentTypeOptions: EditorOption[] = [];
  rewardTypeOptions: EditorOption[] = [
    {id: RewardTypes.item, label: 'Item'},
    {id: RewardTypes.monster, label: 'Monster'},
    {id: RewardTypes.egg, label: 'Egg'}
  ];

  competitionOptions: EditorOption[] = [];
  goalOptions: EditorOption[] = [];

  rewardType = RewardTypes.item;
  isSaving = false;
  currentCompetition: CompetitionModel = null;

  constructor(private integrationSv: AdminIntegrationService,
              private competitionSv: AdminCompetitionService,
              private modalSv: ModalService) {

    for (const level of ObstacleCourseLevels.list()) {
      this.levelOptions.push({id: level, label: this.capitalize(level)});
    }

    for (const contentType of QRCodeContentTypes.list()) {
      this.contentTypeOptions.push({id: contentType, label: contentType});
    }

  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes.competitions) {
      this.competitionOptions = [];
      for (const competition of this.competitions) {
        this.competitionOptions.push({id: competition._id, label: competition.name});
      }
    }

    if (changes.award) {
      this.loadCompetition();
    }

  }

  capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  // Type helpers

  isCompetitionAward(): boolean {
    return this.award.integrationType === IntegrationTypes.competitionAward;
  }

  isObstacleCourse(): boolean {
    return this.award.awardType === CompetitionAwardTypes.obstacleCourse;
  }

  isNew(): boolean {
    return !this.award._id;
  }

  // Changes

  onTypeChanged(type: string) {

    this.award.integrationType = type;

    if (type === IntegrationTypes.award) {
      this.award.competitionId = null;
      this.award.competitionGoalId = null;
      this.currentCompetition = null;
      this.goalOptions = [];
    }

  }

  onAwardTypeChanged(awardType: string) {

    this.award.awardType = awardType;

    if (awardType === CompetitionAwardTypes.obstacleCourse) {

      if (this.award.obstacleCourseLevel === ObstacleCourseLevels.none) {
        this.award.obstacleCourseLevel = ObstacleCourseLevels.beach;
      }

    } else {
      this.award.obstacleCourseLevel = ObstacleCourseLevels.none;
    }

  }

  onCompetitionChanged(competitionId: string) {

    this.award.competitionGoalId = null;

    if (competitionId) {
      this.award.competitionId = competitionId;
    } else {
      this.award.competitionId = null;
    }

    this.loadCompetition();

  }

  // The competition is loaded in full to get its goals and its end date.
  loadCompetition() {

    this.goalOptions = [];
    this.currentCompetition = null;

    if (!this.award || !this.award.competitionId) {
      return;
    }

    const competitionId = this.award.competitionId;
    this.competitionSv.fetchCompetitionById(competitionId).subscribe((competition) => {

      if (this.award.competitionId !== competitionId) {
        return;
      }

      this.currentCompetition = competition;
      this.goalOptions = [];

      for (let i = 0; i < competition.goals.length; i++) {
        const goal = competition.goals[i];
        this.goalOptions.push({id: goal._id, label: `Goal ${i + 1} · ${goal.startValue} – ${goal.endValue}`});
      }

    });

  }

  // Redeem deadline

  getRedeemDateValue(): string {

    if (!this.award.redeemEndAt) {
      return '';
    }

    const date = new Date(this.award.redeemEndAt);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${date.getFullYear()}-${month}-${day}`;

  }

  onRedeemDateChanged(value: string) {

    if (!value) {
      this.award.redeemEndAt = null;
      return;
    }

    const parts = value.split('-');
    this.award.redeemEndAt = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 23, 59, 59);

  }

  /**
   * Which deadline applies. The award's own date wins, then the competition's redeem date or
   * two weeks after the competition ended.
   */
  getDeadlineHint(): string {

    if (this.award.redeemEndAt) {
      return `Can be redeemed until ${this.formatDate(new Date(this.award.redeemEndAt))}.`;
    }

    if (this.isCompetitionAward() && this.currentCompetition) {

      if (this.currentCompetition.redeemEndAt) {
        return `No date set, so the competition's redeem date applies: ${this.formatDate(this.currentCompetition.redeemEndAt)}.`;
      }

      if (this.currentCompetition.endAt) {
        const twoWeeks = new Date(this.currentCompetition.endAt.getTime() + 14 * 24 * 60 * 60 * 1000);
        return `No date set, so it expires two weeks after the competition ends: ${this.formatDate(twoWeeks)}.`;
      }

    }

    return 'No deadline. It can always be redeemed.';

  }

  formatDate(date: Date): string {
    return date.toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'});
  }

  // Rewards

  onAddReward() {

    if (this.rewardType === RewardTypes.item) {

      const itemReward = new ItemRewardModel();
      itemReward.name = 'New item';
      itemReward.numItems = 1;
      this.award.rewards.push(itemReward);

    } else if (this.rewardType === RewardTypes.egg) {

      const eggReward = new EggRewardModel();
      eggReward.name = 'New egg';
      eggReward.itemId = '';
      eggReward.numRequired = 100;
      this.award.rewards.push(eggReward);

    } else if (this.rewardType === RewardTypes.monster) {

      const monsterReward = new MonsterRewardModel();
      monsterReward.name = 'New monster';
      monsterReward.xp = 10;
      monsterReward.abilityOneLevel = 1;
      monsterReward.abilityTwoLevel = 0;
      monsterReward.abilityThreeLevel = 0;
      monsterReward.abilityFourLevel = 0;
      this.award.rewards.push(monsterReward);

    }

  }

  onRemoveReward(index: number) {
    this.award.rewards.splice(index, 1);
  }

  // Saving

  onSave() {

    if (!this.award.name || this.award.name.trim().length === 0) {
      this.modalSv.showAlertModal('Missing name', 'The award needs a name.');
      return;
    }

    if (this.isObstacleCourse() && this.award.obstacleCourseLevel === ObstacleCourseLevels.none) {
      this.modalSv.showAlertModal('Missing level', 'Pick a level for the obstacle course.');
      return;
    }

    this.award.numUsesPerStudent = Number(this.award.numUsesPerStudent);
    this.award.numQuestions = Number(this.award.numQuestions);

    for (const reward of this.award.rewards) {
      this.coerceRewardNumbers(reward);
    }

    for (const reward of this.award.rewards) {

      if (reward.rewardType === RewardTypes.egg) {

        const eggReward = reward as EggRewardModel;
        if (!eggReward.itemId || eggReward.itemId.trim().length === 0 || !(Number(eggReward.numRequired) >= 1)) {
          this.modalSv.showAlertModal('Egg reward', `The egg "${reward.name}" needs an egg id and at least 1 correct answer needed.`);
          return;
        }

      }

    }

    this.isSaving = true;
    this.integrationSv.createOrUpdateIntegration(this.award).subscribe((integration) => {
      this.isSaving = false;
      this.saved.emit(integration);
    }, (err: ErrorModel) => {
      this.isSaving = false;
      this.modalSv.showTranslatedErrorModal(err);
    });

  }

  coerceRewardNumbers(reward: any) {

    const numberFields = ['numItems', 'numRequired', 'xp', 'abilityOneLevel', 'abilityTwoLevel', 'abilityThreeLevel', 'abilityFourLevel'];
    for (const field of numberFields) {
      if (reward[field] !== undefined && reward[field] !== null) {
        reward[field] = Number(reward[field]);
      }
    }

  }

  onDelete() {

    this.modalSv.showChoiceModal(
      'Delete Award',
      'Are you sure you want to delete this award? If it belongs to a goal, the goal will no longer have an award.',
      'Yes', 'No').subscribe(() => {

        this.integrationSv.deleteIntegration(this.award._id).subscribe(() => {
          this.deleted.emit();
        }, (err: ErrorModel) => {
          this.modalSv.showTranslatedErrorModal(err);
        });

      }
    );

  }

  onCancel() {
    this.cancelled.emit();
  }

}
